import React from 'react';
import { FiMail, FiCheckSquare, FiUsers, FiHome, FiSettings, FiLink, FiBell, FiLock } from 'react-icons/fi';
import { usePointerTracking } from '../hooks/usePointerTracking';

const ICON = ({ children, color }) => (
  <span className="icon-wrapper inline-flex items-center justify-center w-8 h-8 rounded text-2xl lg:w-16 lg:h-10 lg:mr-3" style={{ color }}>
    {children}
  </span>
);

const FEATURE_LIST = [
  {
    title: 'Gestion complète des messages professionnels',
    desc: "Créez, lisez, mettez à jour et supprimez des messages facilement. Partagez-les entre équipes, mettez-les en favoris et copiez-collez leur contenu en conservant la mise en page et le formatage. Optimisé pour la messagerie SaaS et la productivité cloud.",
    icon: <FiMail />,
    color: '#3b82f6', // Blue
  },
  {
    title: 'Tâches, rappels & collaboration',
    desc: "Gestion complète des tâches : créez, attribuez, planifiez, suivez l'avancement de vos tâches. Gestion par utilisateur, notifications de rappel et collaboration en temps réel pour les équipes modernes.",
    icon: <FiCheckSquare />,
    color: '#10b981', // Green
  },
  {
    title: 'Gestion avancée des utilisateurs et des rôles',
    desc: "Invitez des membres, attribuez des rôles (owner/admin/member) et contrôlez finement les permissions et l'accès aux fonctionnalités. Sécurité renforcée pour les applications SaaS professionnelles.",
    icon: <FiUsers />,
    color: '#8b5cf6', // Purple
  },
  {
    title: 'Multi-tenant & isolation des données cloud',
    desc: "Un tenant par entité : données, accès et configurations isolés pour chaque organisation ou filiale — sécurité, conformité RGPD et confidentialité intégrées.",
    icon: <FiHome />,
    color: '#fae321', // Amber
  },
  {
    title: 'Personnalisation, thèmes & branding',
    desc: "Adaptez les couleurs, les thèmes (clair/sombre) et la mise en page des messages (sections, en-têtes, blocs), pour une communication qui reflète votre marque et améliore l'expérience utilisateur.",
    icon: <FiSettings />,
    color: '#ef4444', // Red
  },
  {
    title: 'Import / Export & intégrations',
    desc: "Importez et exportez les données (CSV, PDF, JSON) ; webhooks et API pour synchroniser PulseCIO avec vos outils — options avancées pour les plans supérieurs.",
    icon: <FiLink />,
    color: '#06b6d4', // Cyan
  },
  {
    title: 'Mentions, notifications & suivi intelligent',
    desc: "Mentionnez des personnes avec @ et insérez des dates dynamiques avec // (//Jour+1, //Mois-1…) qui se recalculent à chaque lecture. Envoyez des notifications ciblées (in-app) et suivez les lectures et réponses. Optimisé pour la collaboration et la réactivité en entreprise.",
    icon: <FiBell />,
    color: '#3bf751', // Bright Green
  },
  {
    title: 'Sécurité, chiffrement & conformité RGPD',
    desc: "Chiffrement AES-256-GCM de niveau militaire pour toutes vos données (emails, noms, messages, todos). Journalisation des actions, historique immuable et contrôles d'accès. Conformité RGPD garantie.",
    icon: <FiLock />,
    color: '#f97316', // Orange
  },
];

const MARQUEE_ITEMS = [
  'Bibliothèque de messages organisés',
  'Collaboration éditoriale',
  'Tâches et rappels',
  'Multi-tenant sécurisé',
  'Audit & traçabilité',
  'Optimisation de la communication',
];

export const MarqueeBar = () => (
  <div className="relative w-full overflow-x-hidden mb-8">
    <div className="w-full bg-blue-50 dark:bg-blue-900/30 border-y border-blue-200 dark:border-blue-700 py-2 shadow-sm">
      <div className="marquee-track animate-marquee flex w-max whitespace-nowrap text-blue-900 dark:text-blue-200 font-semibold text-sm md:text-base items-center" style={{ minHeight: '4rem' }}>
        {[0, 1].map((dup) => (
          <div className="flex" aria-hidden={dup === 1} key={dup}>
            {MARQUEE_ITEMS.map((item, idx) => (
              <span className="mx-6" key={item + idx + '-' + dup}>
                <strong>{item}</strong>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);

const Features = () => {
  usePointerTracking('.feature-card');

  return (
    <>
      <svg className="sr-only" style={{ overflow: 'visible !important' }} xmlns="http://www.w3.org/2000/svg">
        <filter id="blur-features" width="400%" height="400%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="25" />
        </filter>
      </svg>

      <section id="features" className="py-20">
        <h2 className="text-3xl font-bold mb-4">Ce que PulseCIO fait mieux qu'une pile d'outils</h2>
        <p className="text-gray-600 dark:text-gray-300 mb-4">Aujourd'hui, les DSI jonglent entre <strong>Notion, ClickUp, Confluence, SharePoint et des outils de com interne</strong>. PulseCIO unifie tout ça.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {FEATURE_LIST.map((f, i) => (
            <div
              key={i}
              className="feature-card relative p-6 border dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 flex gap-4 items-center cursor-pointer overflow-hidden group"
              style={{
                '--icon-color': f.color,
                '--pointer-x': -10,
                '--pointer-y': -10,
                transform: 'translate3d(0, 0, 0)',
                transition: 'border-color 0.2s ease, background-color 0.2s ease'
              }}
            >
              {/* Blur background effect */}
              <div
                className="icon-blur-bg absolute inset-0 opacity-0 group-hover:opacity-60 pointer-events-none"
                style={{
                  transform: 'translate3d(calc(var(--pointer-x, -10) * 25%), calc(var(--pointer-y, -10) * 25%), 0) scale(1.8)',
                  filter: 'url(#blur-features) saturate(3) brightness(1.8)',
                  color: f.color,
                  fontSize: '6rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  willChange: 'transform',
                  transition: 'opacity 0.3s ease'
                }}
              >
                {f.icon}
              </div>

              <div className="relative z-10">
                <ICON color={f.color}>{f.icon}</ICON>
              </div>
              <div className="relative z-10">
                <h3 className="text-xl font-semibold mb-2">{f.title}</h3>
                <p className="text-gray-600 dark:text-gray-300">{f.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
};

export default Features;